// src/pages/Instituicoes/DetalhesInstituicaoPage.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Button, 
  Paper, 
  Grid, 
  Box,
  Tabs,
  Tab,
  Chip,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  People as PeopleIcon,
  AccountCircle as AccountCircleIcon,
  SupervisorAccount as SupervisorAccountIcon,
  School as SchoolIcon,
  Storage as StorageIcon,
  Extension as ExtensionIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import SliderInstituicao from '../../components/Instituicoes/SliderInstituicao';
import DestaquesInstituicao from '../../components/Instituicoes/DestaquesInstituicao';
import PostsList from '../../components/Instituicoes/PostsList';
import NewsletterForm from '../../components/Instituicoes/NewsletterForm';
import ListaPerfis from '../../components/Instituicoes/ListaPerfis';
import ConfiguracaoRepositorio from '../../components/Instituicoes/ConfiguracaoRepositorio';
import GestaoModulos from '../../components/Instituicoes/GestaoModulos';
import AtribuirPlano from '../../components/Instituicoes/AtribuirPlano';
import PageHeader from '../../components/Common/PageHeader';
import { obterInstituicao, excluirInstituicao } from '../../services/instituicaoService';
import useAuth from '../../hooks/useAuth';
import api from '../../services/api';

/**
 * Função para determinar a cor do Chip com base no tipo de perfil.
 * @param {string} tipo - Tipo do perfil.
 * @returns {string} - Cor correspondente para o Chip.
 */
const getPerfilColor = (tipo) => {
  switch (tipo.toUpperCase()) {
    case 'GESTAO':
      return 'primary'; // Cor primária para perfis de gestão
    case 'ADMIN':
      return 'secondary'; // Cor secundária para perfis administrativos
    case 'USUARIO':
      return 'default'; // Cor padrão para outros perfis
    default:
      return 'default';
  }
};

/**
 * Função para determinar o ícone do Chip com base no tipo de perfil.
 * @param {string} tipo - Tipo do perfil.
 * @returns {JSX.Element} - Ícone correspondente para o Chip.
 */
const getPerfilIcon = (tipo) => {
  switch (tipo.toUpperCase()) {
    case 'GESTAO':
      return <SupervisorAccountIcon />;
    case 'ADMIN':
      return <AccountCircleIcon />;
    case 'USUARIO':
      return <AccountCircleIcon />;
    default:
      return <AccountCircleIcon />;
  }
};

const DetalhesInstituicaoPage = () => {
  const [instituicao, setInstituicao] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth(); // Obtém o usuário autenticado

  // Estados para gestão de perfis e interface
  const [tabValue, setTabValue] = useState(0);
  const [perfis, setPerfis] = useState([]);
  const [error, setError] = useState('');
  const [loadingPerfis, setLoadingPerfis] = useState(false);
  const [loadingInstituicao, setLoadingInstituicao] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingInstituicao(true);
      setLoadingPerfis(true);
      try {
        const [instituicaoData, perfisData] = await Promise.all([
          obterInstituicao(id),
          api.get(`/perfis-usuario/instituicao/${id}`).then(res => res.data)
        ]);
        setInstituicao(instituicaoData);
        setPerfis(perfisData);
      } catch (err) {
        console.error('Erro ao buscar dados:', err);
        setError('Erro ao carregar dados da instituição.');
      } finally {
        setLoadingInstituicao(false);
        setLoadingPerfis(false);
      }
    };

    fetchData();
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleExcluir = async () => {
    if (window.confirm('Tem certeza que deseja excluir esta instituição?')) {
      try {
        await excluirInstituicao(id);
        navigate('/instituicoes');
      } catch (err) {
        console.error('Erro ao excluir instituição:', err);
        setError('Erro ao excluir a instituição.');
      }
    }
  };

  // Função para atualizar perfis após modificações
  const atualizarPerfis = async () => {
    setLoadingPerfis(true);
    try {
      const perfisData = await api.get(`/perfis-usuario/instituicao/${id}`).then(res => res.data);
      setPerfis(perfisData);
    } catch (err) {
      console.error('Erro ao atualizar perfis:', err);
      setError('Erro ao atualizar perfis.');
    } finally {
      setLoadingPerfis(false);
    }
  };

  if (loadingInstituicao) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!instituicao) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Instituição não encontrada.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={instituicao.nome}
        subtitle={instituicao.tipoInstituicao}
        backTo="/instituicoes"
        breadcrumbs={[{ label: 'Instituições', to: '/instituicoes' }, { label: instituicao.nome }]}
      />

      {/* Slider da Instituição */}
      <SliderInstituicao instituicaoId={id} />

      {/* Informações da Instituição */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h4" gutterBottom>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SchoolIcon sx={{ mr: 1, fontSize: 40 }} />
            {instituicao.nome}
          </Box>
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Tipo:</strong> {instituicao.tipoInstituicao}
            </Typography>
            <Typography>
              <strong>Email:</strong> {instituicao.email}
            </Typography>
            <Typography>
              <strong>Telefone:</strong> {instituicao.telefone}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Endereço:</strong> {instituicao.endereco}
            </Typography>
            <Typography>
              <strong>Website:</strong>{' '}
              <a href={instituicao.website} target="_blank" rel="noopener noreferrer">
                {instituicao.website}
              </a>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography>
              <strong>Descrição:</strong> {instituicao.descricao}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography>
              <strong>Transporte Público:</strong>{' '}
              <Chip 
                label={instituicao.temTransportePublico ? 'Disponível' : 'Não Disponível'} 
                color={instituicao.temTransportePublico ? 'success' : 'default'} 
              />
            </Typography>
          </Grid>
        </Grid>

        {/* Botões de Ação (apenas para administradores ou instituição proprietária) */}
        {(user && (user.tipo === 'admin' || (user.tipo === 'instituicao' && user.instituicaoId === parseInt(id)))) && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/instituicoes/editar/${id}`)}
              sx={{ mr: 1 }}
            >
              Editar
            </Button>
            <Button variant="contained" color="error" onClick={handleExcluir}>
              Excluir
            </Button>
          </Box>
        )}

        {/* Exibir erro se existir */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Divider para separar seções */}
      <Divider sx={{ mt: 4, mb: 4 }} />

      {/* Seção de Perfis */}
      <Paper sx={{ mt: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Informações" />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PeopleIcon sx={{ mr: 1 }} />
                Perfis ({perfis.length})
              </Box>
            } 
          />
          <Tab label="Posts" />
          <Tab label="Destaques" />
          <Tab label={<Box sx={{ display: 'flex', alignItems: 'center' }}><StorageIcon sx={{ mr: 0.5, fontSize: 18 }} />Repositório</Box>} />
          <Tab label={<Box sx={{ display: 'flex', alignItems: 'center' }}><ExtensionIcon sx={{ mr: 0.5, fontSize: 18 }} />Módulos</Box>} />
          <Tab label={<Box sx={{ display: 'flex', alignItems: 'center' }}><CreditCardIcon sx={{ mr: 0.5, fontSize: 18 }} />Plano</Box>} />
        </Tabs>

        {/* Conteúdo das Tabs */}
        <Box sx={{ p: 3 }}>
          {tabValue === 0 && (
            <Grid container spacing={2}>
              {/* Conteúdo existente da tab "Informações" */}
              <Grid item xs={12}>
                <Typography variant="h6">Descrição</Typography>
                <Typography>{instituicao.descricao}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Transporte Público</Typography>
                <Chip 
                  label={instituicao.temTransportePublico ? 'Disponível' : 'Não Disponível'} 
                  color={instituicao.temTransportePublico ? 'success' : 'default'} 
                />
              </Grid>
              {/* Adicione mais informações conforme necessário */}
            </Grid>
          )}

          {tabValue === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6">
                  Perfis da Instituição
                </Typography>
                {(user?.tipo === 'admin' || (user?.tipo === 'instituicao' && user.instituicaoId === parseInt(id))) && (
                  <Button
                    variant="contained"
                    startIcon={<AccountCircleIcon />}
                    onClick={() => navigate(`/instituicoes/${id}/perfis/adicionar`)}
                  >
                    Adicionar Perfil
                  </Button>
                )}
              </Box>

              {loadingPerfis ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <ListaPerfis 
                  perfis={perfis}
                  instituicaoId={id}
                  onUpdate={atualizarPerfis}
                  getPerfilColor={getPerfilColor} // Passando a função como prop se necessário
                  getPerfilIcon={getPerfilIcon}   // Passando a função como prop se necessário
                />
              )}
            </Box>
          )}

          {tabValue === 2 && (
            <Box>
              <PostsList instituicaoId={id} />
            </Box>
          )}

          {tabValue === 3 && (
            <Box>
              <DestaquesInstituicao instituicaoId={id} />
            </Box>
          )}

          {tabValue === 4 && (
            <ConfiguracaoRepositorio instituicaoId={id} instituicao={instituicao} />
          )}

          {tabValue === 5 && (
            <GestaoModulos instituicaoId={id} />
          )}

          {tabValue === 6 && (
            <AtribuirPlano instituicaoId={id} />
          )}
        </Box>
      </Paper>

      {/* Newsletter Form no final */}
      <Box sx={{ mt: 4 }}>
        <NewsletterForm instituicaoId={id} />
      </Box>
    </Box>
  );
};

export default DetalhesInstituicaoPage;
